import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Bell, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import analyticsService from '../services/analyticsService';

const Notifications = ({ userId = 'demo_user' }) => {
  const [notifications, setNotifications] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [showAlertForm, setShowAlertForm] = useState(false);
  const [alertForm, setAlertForm] = useState({
    token: 'TON',
    condition: 'above',
    targetPrice: '',
  });

  const fetchNotifications = async () => {
    try {
      const data = await analyticsService.getNotifications(userId, false, 20);
      setNotifications(data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const data = await analyticsService.getActiveAlerts(userId);
      setAlerts(data);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchAlerts();
    const interval = setInterval(() => {
      fetchNotifications();
      fetchAlerts();
    }, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [userId]);

  const handleMarkAllRead = async () => {
    try {
      await analyticsService.markNotificationsRead(userId);
      toast.success('All notifications marked as read');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const handleCreateAlert = async (e) => {
    e.preventDefault();
    
    if (!alertForm.targetPrice || parseFloat(alertForm.targetPrice) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    try {
      await analyticsService.createPriceAlert(
        userId,
        alertForm.token,
        alertForm.condition,
        parseFloat(alertForm.targetPrice)
      );
      
      toast.success('Price alert created!');
      setShowAlertForm(false);
      setAlertForm({ token: 'TON', condition: 'above', targetPrice: '' });
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to create alert');
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="space-y-6" data-testid="notifications-container">
      {/* Notifications Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notifications
                {unreadCount > 0 && (
                  <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                    {unreadCount}
                  </span>
                )}
              </CardTitle>
              <CardDescription>Stay updated with your portfolio</CardDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
            >
              <Check className="h-4 w-4 mr-2" />
              Mark All Read
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No notifications yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notifications.slice(0, 5).map((notification, index) => (
                <div
                  key={index}
                  className={`p-3 border rounded-lg ${
                    notification.read ? 'bg-white' : 'bg-blue-50 border-blue-200'
                  }`}
                  data-testid={`notification-${index}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{notification.title}</p>
                      <p className="text-xs text-gray-600 mt-1">{notification.message}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(notification.created_at).toLocaleString()}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="h-2 w-2 bg-blue-600 rounded-full ml-2 mt-1"></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Price Alerts Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Price Alerts</CardTitle>
              <CardDescription>Get notified when prices hit your targets</CardDescription>
            </div>
            <Button
              size="sm"
              onClick={() => setShowAlertForm(!showAlertForm)}
              data-testid="toggle-alert-form-btn"
            >
              {showAlertForm ? <X className="h-4 w-4 mr-2" /> : <Bell className="h-4 w-4 mr-2" />}
              {showAlertForm ? 'Cancel' : 'New Alert'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Alert Form */}
          {showAlertForm && (
            <form onSubmit={handleCreateAlert} className="space-y-4 mb-6 p-4 border rounded-lg bg-gray-50">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Token</Label>
                  <Select value={alertForm.token} onValueChange={(v) => setAlertForm({...alertForm, token: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TON">TON</SelectItem>
                      <SelectItem value="USDT">USDT</SelectItem>
                      <SelectItem value="USDC">USDC</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Condition</Label>
                  <Select value={alertForm.condition} onValueChange={(v) => setAlertForm({...alertForm, condition: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="above">Above</SelectItem>
                      <SelectItem value="below">Below</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target Price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="50.00"
                    value={alertForm.targetPrice}
                    onChange={(e) => setAlertForm({...alertForm, targetPrice: e.target.value})}
                  />
                </div>
              </div>
              <Button type="submit" className="w-full">
                Create Alert
              </Button>
            </form>
          )}

          {/* Active Alerts */}
          {alerts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No active alerts</p>
              <p className="text-sm mt-2">Create an alert to get notified</p>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((alert, index) => (
                <div
                  key={index}
                  className="p-3 border rounded-lg bg-white"
                  data-testid={`alert-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {alert.token} {alert.condition} ${alert.target_price}
                      </p>
                      <p className="text-xs text-gray-500">
                        Created: {new Date(alert.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                      Active
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Notifications;
